// src/components/ModalNovoVeiculo.jsx
import React, { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';

const initialState = {
  placa: '',
  marca: '',
  modelo: '',
  ano: '',
  cor: '',
  tipo: 'Carro',
  km: '',
  obs: '',
};

const ModalNovoVeiculo = ({ open, onClose, onSave, veiculo, editando }) => {
  const [form, setForm] = useState(initialState);

  useEffect(() => {
    if (open) {
      setForm(editando && veiculo ? { ...initialState, ...veiculo } : initialState);
    }
  }, [open, veiculo, editando]);

  if (!open) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'placa') {
      setForm((prev) => ({ ...prev, placa: value.toUpperCase().replace(/\s+/g, '') }));
      return;
    }
    if (name === 'km' || name === 'ano') {
      const onlyDigits = value.replace(/\D/g, '');
      setForm((prev) => ({ ...prev, [name]: onlyDigits }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveClick = () => {
    if (!form.placa || !form.marca || !form.modelo) {
      Swal.fire({
        icon: 'warning',
        title: 'Campos obrigatórios',
        text: 'Informe Placa, Marca e Modelo do veículo.',
        confirmButtonColor: '#2563eb',
      });
      return;
    }

    const payload = {
      ...form,
      placa: (form.placa || '').toUpperCase().trim(),
      km: form.km !== '' ? Number(form.km) : null,
      ano: form.ano !== '' ? Number(form.ano) : null,
      tipo: form.tipo || 'Carro',
    };

    onSave(payload);
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-[2px] animate-fadein"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl w-full max-w-2xl relative animate-modalSlideIn overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="max-h-[94vh] flex flex-col">
          {/* Header */}
          <div className="p-7 pb-0 relative">
            <button
              className="absolute right-8 top-7 text-gray-500 hover:text-[#004072] dark:text-gray-200 dark:hover:text-white text-base font-medium"
              onClick={onClose}
            >
              Fechar
            </button>
            <h2 className="text-2xl font-bold mb-6 text-[#004072] dark:text-white">
              {editando ? 'Editar Veículo' : 'Novo Veículo'}
            </h2>
          </div>

          {/* Body */}
          <div className="px-7 pb-6 space-y-6 overflow-y-auto flex-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <InputField
                label="Placa"
                name="placa"
                value={form.placa || ''}
                onChange={handleChange}
                placeholder="ABC1D23 ou ABC-1234"
                required
              />
              <SelectField
                label="Tipo"
                name="tipo"
                value={form.tipo || 'Carro'}
                onChange={handleChange}
                options={['Carro', 'Moto', 'Caminhão']}
              />
              <InputField
                label="Marca"
                name="marca"
                value={form.marca || ''}
                onChange={handleChange}
                placeholder="Ex: Fiat"
                required
              />
              <InputField
                label="Modelo"
                name="modelo"
                value={form.modelo || ''}
                onChange={handleChange}
                placeholder="Ex: Uno"
                required
              />
              <InputField
                label="Ano"
                name="ano"
                type="number"
                value={form.ano || ''}
                onChange={handleChange}
                placeholder="Ex: 2020"
              />
              <InputField
                label="Cor"
                name="cor"
                value={form.cor || ''}
                onChange={handleChange}
                placeholder="Ex: Prata"
              />
              <InputField
                label="Quilometragem (KM)"
                name="km"
                type="number"
                value={form.km || ''}
                onChange={handleChange}
                placeholder="Ex: 50000"
                className="sm:col-span-2"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1 dark:text-gray-200">
                Observações
              </label>
              <textarea
                name="obs"
                value={form.obs || ''}
                onChange={handleChange}
                rows={3}
                className="border rounded-xl px-4 py-3 w-full bg-gray-50 dark:bg-gray-800 dark:text-white dark:border-gray-700 focus:ring-2 focus:ring-[#004AAD] transition"
                placeholder="Detalhes sobre o veículo..."
              />
            </div>
          </div>

          {/* Footer fixo arredondado */}
          <div className="px-7 py-4 bg-white/95 dark:bg-gray-900/95 backdrop-blur border-t dark:border-gray-800 flex justify-end gap-3 rounded-b-3xl">
            <button
              onClick={onClose}
              className="px-5 py-3 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 font-semibold transition"
            >
              Cancelar
            </button>
            <button
              onClick={handleSaveClick}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 py-3 font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            >
              <Save size={18} /> Salvar
            </button>
          </div>
        </div>

        {/* Animações no padrão ModalNovaOS */}
        <style>{`
          .animate-modalSlideIn { animation: modalSlideIn 0.35s cubic-bezier(.32,.72,.57,1) both; }
          @keyframes modalSlideIn {
            0% { opacity:0; transform: translateY(60px) scale(.97); }
            100% { opacity:1; transform: translateY(0) scale(1); }
          }
          .animate-fadein { animation: fadein 0.25s; }
          @keyframes fadein { from { opacity: 0; } to { opacity: 1; } }
        `}</style>
      </div>
    </div>
  );
};

/* ---------- Componentes auxiliares estilizados ---------- */
const InputField = ({ label, className = '', ...props }) => (
  <div className={className}>
    <label className="block text-sm font-semibold mb-1 dark:text-gray-200">{label}</label>
    <input
      {...props}
      className="border rounded-xl px-4 py-3 w-full bg-gray-50 dark:bg-gray-800 dark:text-white dark:border-gray-700 focus:ring-2 focus:ring-[#004AAD] transition"
    />
  </div>
);

const SelectField = ({ label, options, className = '', ...props }) => (
  <div className={className}>
    <label className="block text-sm font-semibold mb-1 dark:text-gray-200">{label}</label>
    <select
      {...props}
      className="border rounded-xl px-4 py-3 w-full bg-gray-50 dark:bg-gray-800 dark:text-white dark:border-gray-700 focus:ring-2 focus:ring-[#004AAD] transition"
    >
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  </div>
);

export default ModalNovoVeiculo;
